import { useState } from "react";
import { fmtEUR, fmtPct } from "@/lib/format";
import { FileSpreadsheet, FileText, TrendingUp, PiggyBank, Landmark, Sigma, Receipt, ChevronDown, ChevronRight, Package } from "lucide-react";
import { exportToExcel, exportToPDF } from "@/lib/exports";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import { motion } from "framer-motion";

const BENI_COLORS = ["#1AAB67", "#0A0A0A", "#F59E0B", "#3B82F6", "#8B5CF6", "#EF4444", "#14B8A6", "#EC4899", "#6366F1", "#84CC16"];

// Soglia oltre la quale i costi della pratica non sono più tabellari
// ma vanno negoziati previa analisi di fattibilità.
const PRATICA_SOGLIA_FATTIBILITA = 200000;

const PRATICA_FASCE = [
  { max: 20000, gse: 1200, perizia: 1200, revisore: 900 },
  { max: 50000, gse: 1400, perizia: 1800, revisore: 1000 },
  { max: 100000, gse: 1700, perizia: 2700, revisore: 1800 },
  { max: 200000, gse: 1900, perizia: 4200, revisore: 2100 },
];
const SABATINI_PRATICA_FISSA = 350;
const SABATINI_PRATICA_PCT = 0.07;

function computeSpesePratica(result) {
  const investimento = result.input.investimento || 0;
  // Se l'investimento totale supera la soglia, i costi non sono tabellari.
  if (investimento > PRATICA_SOGLIA_FATTIBILITA) {
    return { fattibilita: true };
  }
  const beneficio = result.beneficio_iperammortamento || 0;
  const fascia = PRATICA_FASCE.find((f) => beneficio <= f.max) || PRATICA_FASCE[PRATICA_FASCE.length - 1];
  const sabatini_pratica =
    result.sabatini_beneficio > 0
      ? SABATINI_PRATICA_FISSA + result.sabatini_beneficio * SABATINI_PRATICA_PCT
      : 0;
  const totale = fascia.gse + fascia.perizia + fascia.revisore + sabatini_pratica;
  return { fattibilita: false, fascia, sabatini_pratica, totale };
}

export default function ResultsPanel({ result, onRequireAuth, isLogged = true, actionsSlot = null, beniSlot = null }) {
  const [showDetails, setShowDetails] = useState(false);
  const [beneOpen, setBeneOpen] = useState({});
  const piani_per_bene = result.piani_per_bene || [];
  const hasBreakdown = piani_per_bene.length > 0;

  // Se attivo dettaglio per bene, il grafico è "stacked" con una barra per anno divisa per bene (vantaggio fiscale)
  const chartData = hasBreakdown
    ? (() => {
        const byYear = {};
        piani_per_bene.forEach((pb) => {
          pb.piano.forEach((r) => {
            if (!byYear[r.anno]) byYear[r.anno] = { anno: r.anno };
            byYear[r.anno][pb.label] = (byYear[r.anno][pb.label] || 0) + r.vantaggio_fiscale;
          });
        });
        return Object.values(byYear).sort((a, b) => a.anno - b.anno);
      })()
    : result.piano_ammortamento.map((r) => ({
        anno: r.anno,
        vantaggio: r.vantaggio_fiscale,
        ordinario: r.ammortamento_ordinario,
        iper: r.iperammortamento,
      }));
  const spese = computeSpesePratica(result);
  const fasciaLabel = spese.fattibilita
    ? "Investimento oltre 200k€"
    : spese.fascia.max === 20000 ? "0-20k€"
    : spese.fascia.max === 50000 ? "20-50k€"
    : spese.fascia.max === 100000 ? "50-100k€"
    : "100-200k€";
  const vantaggioNetto = spese.fattibilita
    ? null
    : result.vantaggio_complessivo - spese.totale;

  const handleExport = async (fn) => {
    await fn(result);
  };

  return (
    <div className="space-y-6">
      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KPI
          icon={<Sigma className="w-4 h-4" />}
          label="Maggiorazione totale"
          value={fmtEUR(result.maggiorazione_totale)}
          testid="kpi-maggiorazione"
        />
        <KPI
          icon={<Receipt className="w-4 h-4" />}
          label="Detrazione ordinaria (IRES+IRAP)"
          value={fmtEUR(result.detrazione_ordinaria_totale)}
          testid="kpi-detrazione"
        />
        <KPI
          icon={<TrendingUp className="w-4 h-4" />}
          label="Beneficio Iperammortamento"
          value={fmtEUR(result.beneficio_iperammortamento)}
          sub={fmtPct(result.beneficio_iperammortamento_pct)}
          accent
          testid="kpi-iperammortamento"
        />
        <KPI
          icon={<Landmark className="w-4 h-4" />}
          label="Nuova Sabatini"
          value={fmtEUR(result.sabatini_beneficio)}
          sub={
            result.sabatini_beneficio > 0
              ? `su ${fmtEUR(result.sabatini_base_utilizzato)} · ${fmtPct(result.sabatini_percentuale)}`
              : null
          }
          testid="kpi-sabatini"
        />
        <KPI
          icon={<PiggyBank className="w-4 h-4" />}
          label="Vantaggio complessivo"
          value={fmtEUR(result.vantaggio_complessivo)}
          sub={fmtPct(result.vantaggio_complessivo_pct)}
          testid="kpi-vantaggio"
        />
      </div>

      {/* Summary bar */}
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white border border-line rounded-lg shadow-card p-6"
        data-testid="summary-card"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">Investimento lordo</div>
            <div className="font-mono text-2xl text-ink mt-1">{fmtEUR(result.input.investimento)}</div>
          </div>
          <div className="border-l border-line pl-6">
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">Investimento netto post agevolazioni</div>
            <div className="font-mono text-3xl text-brand mt-1" data-testid="effective-net">{fmtEUR(result.investimento_effettivo_netto)}</div>
            <div className="mt-1 text-[11px] text-ink-muted">
              Risparmio effettivo: <span className="font-mono">{fmtPct(result.vantaggio_complessivo_pct)}</span>
            </div>
          </div>
          <div className="flex md:justify-end gap-2">
            <button
              onClick={() => handleExport(exportToPDF)}
              data-testid="export-pdf"
              title="Scarica PDF"
              className="inline-flex items-center gap-2 px-4 h-10 border border-line rounded-md text-sm font-medium bg-white hover:bg-line/40"
            >
              <FileText className="w-4 h-4" />
              PDF
            </button>
            <button
              onClick={() => handleExport(exportToExcel)}
              data-testid="export-excel"
              title="Scarica Excel"
              className="inline-flex items-center gap-2 px-4 h-10 bg-brand text-bg rounded-md text-sm font-medium hover:-translate-y-[1px]"
            >
              <FileSpreadsheet className="w-4 h-4" />
              Excel
            </button>
          </div>
        </div>
      </motion.div>

      {/* Spese pratica */}
      <div className="bg-white border border-line rounded-lg shadow-card overflow-hidden" data-testid="spese-pratica">
        <div className="p-6 border-b border-line flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded bg-brand/10 text-brand flex items-center justify-center">
              <Receipt className="w-5 h-5" />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">
                {spese.fattibilita ? "Fascia custom" : `Fascia ${fasciaLabel} · su beneficio iperammortamento`}
              </div>
              <h3 className="font-display text-lg font-semibold text-ink">Spese pratica</h3>
            </div>
          </div>
          {!spese.fattibilita && (
            <div className="text-right">
              <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">Totale</div>
              <div className="font-mono text-2xl text-ink" data-testid="spese-totale">{fmtEUR(spese.totale)}</div>
            </div>
          )}
        </div>
        {spese.fattibilita ? (
          <div className="p-6" data-testid="spese-previa-fattibilita">
            <div className="bg-brand/[0.06] border border-brand/30 rounded-md p-5">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-brand text-white flex items-center justify-center flex-shrink-0">
                  <Receipt className="w-4 h-4" />
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-wider text-brand">Investimento oltre 200.000€</div>
                  <p className="mt-1 text-sm text-ink leading-relaxed">
                    <strong>I costi della pratica sono da considerarsi previa fattibilità.</strong>
                    <br />
                    Per investimenti che superano i 200.000€ le tariffe standard non sono più applicabili:
                    l&apos;incarico viene quotato caso per caso previa analisi di fattibilità tecnica
                    ed economica.
                  </p>
                  <a
                    href="/contattaci"
                    data-testid="spese-fattibilita-cta"
                    className="mt-4 inline-flex items-center gap-2 px-4 h-10 bg-brand text-white font-display uppercase tracking-widest text-xs rounded hover:bg-brand-dark hover:shadow-brand-glow"
                  >
                    Richiedi analisi di fattibilità
                  </a>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-line/40">
                <tr>
                  <Th>Voce</Th>
                  <Th right>Importo</Th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-line">
                  <Td>Presentazione e gestione portale GSE</Td>
                  <Td right mono>{fmtEUR(spese.fascia.gse)}</Td>
                </tr>
                <tr className="border-b border-line">
                  <Td>Costo tecnico perizia</Td>
                  <Td right mono>{fmtEUR(spese.fascia.perizia)}</Td>
                </tr>
                <tr className="border-b border-line">
                  <Td>Corso revisore + visto di conformità</Td>
                  <Td right mono>{fmtEUR(spese.fascia.revisore)}</Td>
                </tr>
                {spese.sabatini_pratica > 0 && (
                  <tr className="border-b border-line bg-brand/[0.03]">
                    <Td>Presentazione pratica Nuova Sabatini <span className="text-ink-muted text-xs">(€ 350 + 7% su beneficio)</span></Td>
                    <Td right mono>{fmtEUR(spese.sabatini_pratica)}</Td>
                  </tr>
                )}
                <tr className="bg-line/50 font-semibold">
                  <Td strong>Totale spese pratica</Td>
                  <Td right mono strong>{fmtEUR(spese.totale)}</Td>
                </tr>
                <tr className="bg-brand/[0.06]">
                  <Td accent><span className="font-semibold">Vantaggio netto stimato</span> <span className="text-ink-muted text-xs font-normal">(vantaggio complessivo − spese pratica)</span></Td>
                  <Td right mono accent><span className="text-lg font-semibold">{fmtEUR(vantaggioNetto)}</span></Td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
        {/* Disclaimer */}
        <div className="border-t border-line bg-bg px-6 py-4" data-testid="disclaimer">
          <div className="flex gap-3 text-[11px] text-ink-muted leading-relaxed">
            <span className="font-display text-[10px] uppercase tracking-widest text-brand flex-shrink-0 mt-0.5">Nota</span>
            <p>
              I valori mostrati sono <strong className="text-ink">stime indicative</strong> calcolate su
              coefficienti di legge, aliquote fiscali dichiarate e tariffe medie di mercato per le spese
              di pratica. L'effettivo beneficio dipende dalla situazione fiscale specifica dell'impresa,
              dalla qualificazione dei beni ai sensi degli Allegati IV e V alla L. 199/2025 e s.m.i., dagli esiti
              della prenotazione GSE e dalla verifica sostanziale dei requisiti. Nessun importo
              costituisce garanzia di erogazione o consulenza fiscale definitiva.{" "}
              <strong className="text-ink">Verifica sempre i risultati con il tuo commercialista o
              con un consulente fiscale</strong> prima di assumere impegni
              contrattuali o presentare pratiche.
            </p>
          </div>
        </div>
      </div>


      {/* Actions slot: pulsanti "Salva pratica" e "Calcolo avanzato" iniettati dal parent */}
      {actionsSlot}

      {/* Beni slot: sezione "Calcolo avanzato per singolo bene" (sopra il Dettaglio) */}
      {beniSlot}

      {/* Dettaglio: Grafico + Piano ammortamento a discesa */}
      <div className="bg-white border border-line rounded-lg shadow-card overflow-hidden" data-testid="dettaglio-accordion">
        <button
          type="button"
          onClick={() => setShowDetails((v) => !v)}
          data-testid="dettaglio-toggle"
          className="w-full p-6 flex items-center justify-between hover:bg-line/20 transition text-left"
        >
          <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">Dettaglio</div>
            <h3 className="font-display text-lg font-semibold text-ink">
              Grafico e piano di ammortamento pluriennale
            </h3>
            <p className="mt-1 text-xs text-ink-muted">
              Vantaggio fiscale anno per anno + piano di ammortamento completo.
            </p>
          </div>
          <ChevronDown
            className={`w-5 h-5 text-brand flex-shrink-0 transition-transform ${showDetails ? "rotate-180" : ""}`}
          />
        </button>

        {showDetails && (
          <div className="p-6 border-t border-line space-y-6" data-testid="dettaglio-content">

      {/* Chart (spostato sotto le spese pratica) */}
      <div className="bg-white border border-line rounded-lg shadow-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">Grafico</div>
            <h3 className="font-display text-lg font-semibold text-ink">
              {hasBreakdown
                ? "Vantaggio fiscale per bene anno per anno"
                : "Vantaggio fiscale anno per anno"}
            </h3>
          </div>
          {hasBreakdown && (
            <div className="flex flex-wrap gap-2" data-testid="chart-legend">
              {piani_per_bene.map((pb, idx) => (
                <div key={pb.label} className="inline-flex items-center gap-1.5 text-[11px] text-ink-muted">
                  <span
                    className="w-3 h-3 rounded-sm"
                    style={{ backgroundColor: BENI_COLORS[idx % BENI_COLORS.length] }}
                  />
                  {pb.label}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="h-[300px]" data-testid="chart-container">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="2 2" stroke="#E5E7EB" vertical={false} />
              <XAxis dataKey="anno" tick={{ fontFamily: "IBM Plex Mono", fontSize: 11, fill: "#5C5852" }} axisLine={{ stroke: "#E6E2DA" }} tickLine={false} />
              <YAxis
                tick={{ fontFamily: "IBM Plex Mono", fontSize: 11, fill: "#5C5852" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)}
              />
              <Tooltip
                contentStyle={{ background: "#fff", border: "1px solid #E6E2DA", borderRadius: 6, fontSize: 12, fontFamily: "IBM Plex Sans" }}
                formatter={(v) => fmtEUR(v)}
                labelStyle={{ fontFamily: "IBM Plex Mono", color: "#1C1A17" }}
              />
              {hasBreakdown ? (
                piani_per_bene.map((pb, idx) => (
                  <Bar
                    key={pb.label}
                    dataKey={pb.label}
                    stackId="beni"
                    fill={BENI_COLORS[idx % BENI_COLORS.length]}
                    radius={idx === piani_per_bene.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]}
                  />
                ))
              ) : (
                <Bar dataKey="vantaggio" name="Vantaggio fiscale" radius={[3, 3, 0, 0]}>
                  {chartData.map((_, i) => (
                    <Cell key={i} fill={i === 0 ? "#1AAB67" : "#0A0A0A"} />
                  ))}
                </Bar>
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>


      {/* Piano ammortamento */}
      <div className="bg-white border border-line rounded-lg shadow-card overflow-hidden">
        <div className="p-6 border-b border-line flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">Piano</div>
            <h3 className="font-display text-lg font-semibold text-ink">Ammortamento pluriennale</h3>
          </div>
          <span className="font-mono text-xs text-ink-muted">{result.piano_ammortamento.length} anni</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="plan-table">
            <thead className="bg-line/40 sticky top-0">
              <tr>
                <Th>Anno</Th>
                <Th right>Aliquota</Th>
                <Th right>Amm. Ordinario</Th>
                <Th right>Iperammortamento</Th>
                <Th right>Amm. Complessivo</Th>
                <Th right>Detrazione ordinaria</Th>
                <Th right accent>Vantaggio Fiscale</Th>
              </tr>
            </thead>
            <tbody>
              {result.piano_ammortamento.map((r, i) => (
                <tr key={r.anno} className="row-anim border-b border-line last:border-0 hover:bg-line/30" style={{ animationDelay: `${i * 30}ms` }} data-testid={`plan-row-${r.anno}`}>
                  <Td>{r.anno}</Td>
                  <Td right mono>{fmtPct(r.aliquota, 2)}</Td>
                  <Td right mono>{fmtEUR(r.ammortamento_ordinario)}</Td>
                  <Td right mono>{fmtEUR(r.iperammortamento)}</Td>
                  <Td right mono strong>{fmtEUR(r.ammortamento_complessivo)}</Td>
                  <Td right mono>{fmtEUR(r.detrazione_ordinaria || 0)}</Td>
                  <Td right mono accent>{fmtEUR(r.vantaggio_fiscale)}</Td>
                </tr>
              ))}
              <tr className="bg-line/50 font-semibold">
                <Td>Totale</Td>
                <Td right>—</Td>
                <Td right mono>{fmtEUR(result.piano_ammortamento.reduce((s, r) => s + r.ammortamento_ordinario, 0))}</Td>
                <Td right mono>{fmtEUR(result.piano_ammortamento.reduce((s, r) => s + r.iperammortamento, 0))}</Td>
                <Td right mono>{fmtEUR(result.piano_ammortamento.reduce((s, r) => s + r.ammortamento_complessivo, 0))}</Td>
                <Td right mono>{fmtEUR(result.piano_ammortamento.reduce((s, r) => s + (r.detrazione_ordinaria || 0), 0))}</Td>
                <Td right mono accent>{fmtEUR(result.piano_ammortamento.reduce((s, r) => s + r.vantaggio_fiscale, 0))}</Td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Piano per singolo bene (accordion, solo se breakdown attivo) */}
      {hasBreakdown && (
        <div className="bg-white border border-line rounded-lg shadow-card overflow-hidden" data-testid="piani-per-bene">
          <div className="p-6 border-b border-line">
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">Dettaglio</div>
            <h3 className="font-display text-lg font-semibold text-ink">Piano ammortamento per singolo bene</h3>
            <p className="mt-1 text-xs text-ink-muted">
              Il piano complessivo qui sopra è la somma dei piani per singolo bene indicati sotto.
            </p>
          </div>
          <div className="divide-y divide-line">
            {piani_per_bene.map((pb, idx) => {
              const open = !!beneOpen[pb.label];
              const totali = {
                ord: pb.piano.reduce((s, r) => s + r.ammortamento_ordinario, 0),
                iper: pb.piano.reduce((s, r) => s + r.iperammortamento, 0),
                vant: pb.piano.reduce((s, r) => s + r.vantaggio_fiscale, 0),
              };
              return (
                <div key={pb.label} data-testid={`bene-block-${idx}`}>
                  <button
                    onClick={() => setBeneOpen((o) => ({ ...o, [pb.label]: !o[pb.label] }))}
                    data-testid={`bene-toggle-${idx}`}
                    className="w-full flex items-center gap-3 px-6 py-4 hover:bg-line/20 text-left"
                  >
                    <span
                      className="w-3 h-3 rounded-sm flex-shrink-0"
                      style={{ backgroundColor: BENI_COLORS[idx % BENI_COLORS.length] }}
                    />
                    <Package className="w-4 h-4 text-ink-muted flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-ink truncate">{pb.label}</div>
                      <div className="font-mono text-[11px] text-ink-muted">
                        {fmtEUR(pb.importo)} · aliquota {fmtPct(pb.aliquota, 2)} · {pb.piano.length} anni
                      </div>
                    </div>
                    <div className="hidden sm:flex flex-col text-right mr-3">
                      <span className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">Vantaggio</span>
                      <span className="font-mono text-brand font-semibold text-sm">{fmtEUR(totali.vant)}</span>
                    </div>
                    {open ? (
                      <ChevronDown className="w-5 h-5 text-ink-muted" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-ink-muted" />
                    )}
                  </button>
                  {open && (
                    <div className="overflow-x-auto bg-bg" data-testid={`bene-plan-${idx}`}>
                      <table className="w-full text-sm">
                        <thead className="bg-line/40">
                          <tr>
                            <Th>Anno</Th>
                            <Th right>Aliquota</Th>
                            <Th right>Amm. Ordinario</Th>
                            <Th right>Iperammortamento</Th>
                            <Th right>Amm. Complessivo</Th>
                            <Th right>Detr. ordinaria</Th>
                            <Th right accent>Vantaggio Fiscale</Th>
                          </tr>
                        </thead>
                        <tbody>
                          {pb.piano.map((r) => (
                            <tr key={r.anno} className="border-b border-line last:border-0 hover:bg-line/30">
                              <Td>{r.anno}</Td>
                              <Td right mono>{fmtPct(r.aliquota, 2)}</Td>
                              <Td right mono>{fmtEUR(r.ammortamento_ordinario)}</Td>
                              <Td right mono>{fmtEUR(r.iperammortamento)}</Td>
                              <Td right mono strong>{fmtEUR(r.ammortamento_complessivo)}</Td>
                              <Td right mono>{fmtEUR(r.detrazione_ordinaria || 0)}</Td>
                              <Td right mono accent>{fmtEUR(r.vantaggio_fiscale)}</Td>
                            </tr>
                          ))}
                          <tr className="bg-line/50 font-semibold">
                            <Td>Totale</Td>
                            <Td right>—</Td>
                            <Td right mono>{fmtEUR(totali.ord)}</Td>
                            <Td right mono>{fmtEUR(totali.iper)}</Td>
                            <Td right mono>{fmtEUR(totali.ord + totali.iper)}</Td>
                            <Td right mono>{fmtEUR(pb.piano.reduce((s, r) => s + (r.detrazione_ordinaria || 0), 0))}</Td>
                            <Td right mono accent>{fmtEUR(totali.vant)}</Td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

          </div>
        )}
      </div>
      {/* / Dettaglio accordion */}

      <div className="text-[11px] text-ink-muted bg-white/50 border border-line rounded p-3">
        <strong>Nota:</strong> le stime sono indicative e si basano sui parametri della normativa vigente. Per le pratiche 2026 l'aliquota di riferimento è quella dell'Iperammortamento 2026 (L. 199/2025, art. 1 c. 427-436). I risultati devono essere confermati da un consulente fiscale prima di assumere impegni.
      </div>
    </div>
  );
}
function KPI({ icon, label, value, sub, accent, testid }) {
  return (
    <div className={`bg-white border ${accent ? "border-brand/40" : "border-line"} rounded-lg p-5 shadow-card`} data-testid={testid}>
      <div className={`flex items-center gap-2 ${accent ? "text-brand" : "text-ink-muted"}`}>{icon}
        <span className="font-mono text-[10px] uppercase tracking-wider">{label}</span>
      </div>
      <div className={`mt-3 font-mono text-2xl ${accent ? "text-brand font-semibold" : "text-ink"}`}>{value}</div>
      {sub && <div className="mt-1 font-mono text-xs text-ink-muted">{sub}</div>}
    </div>
  );
}

function Th({ children, right, accent }) {
  return (
    <th className={`px-4 py-3 text-[10px] font-mono uppercase tracking-wider ${right ? "text-right" : "text-left"} ${accent ? "text-brand" : "text-ink-muted"}`}>
      {children}
    </th>
  );
}

function Td({ children, right, mono, strong, accent }) {
  return (
    <td className={`px-4 py-2.5 ${right ? "text-right" : "text-left"} ${mono ? "font-mono num-pill" : ""} ${strong ? "font-semibold" : ""} ${accent ? "text-brand font-semibold" : "text-ink"}`}>
      {children}
    </td>
  );
}
